import { createEdge, createNode } from '@ui/lib/reactFlow/helpers'
import { getOutgoers, Node } from 'reactflow'

import { NodeType } from '@ui/__generated__/graphql'
import { NodeButtonPosition, SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { uuid } from '@ui/lib/utils'
import { useNodeAndEdgeStore } from '@ui/stores'

export const createNodeInBetween = ({
  edgeIds,
  position,
  type,
  description,
}: {
  edgeIds: string[]
  position: NodeButtonPosition
  type?: NodeType
  description?: string
}) => {
  const edges = useNodeAndEdgeStore.getState().edges
  const nodes = useNodeAndEdgeStore.getState().nodes
  const setNodes = useNodeAndEdgeStore.getState().setNodes
  const setEdges = useNodeAndEdgeStore.getState().setEdges

  const setRefreshLayout = useNodeAndEdgeStore.getState().setRefreshLayout

  const isLeft = position === NodeButtonPosition.LEFT

  let parentNodeId: string = ''
  let currentNodeId: string = ''
  let childNodeIds: Node<unknown, string | undefined>[] = []

  if (isLeft) {
    parentNodeId = edges.find((edge) => edge.id === edgeIds[0])!.source
    currentNodeId = nodes.find((node) => edges.find((edge) => edge.id === edgeIds[0])!.target === node.id)!.id
  } else {
    currentNodeId = edges.find((edge) => edge.id === edgeIds[0])!.source
    childNodeIds = getOutgoers(nodes.find((node) => node.id === currentNodeId) as Node, nodes, edges)
  }

  // Create New Node
  const newNodeId = uuid()

  const newNode = createNode({
    nodeId: newNodeId,
    nodeType: type || NodeType.Placeholder,
    parentNodeId: isLeft ? parentNodeId : currentNodeId,
    description,
  })

  // Create New Edges
  const logEdge = createEdge({
    source: isLeft ? parentNodeId : currentNodeId,
    target: newNodeId,
    targetHandle: TargetHandleEnum.TARGET_LOGS,
    sourceHandle: SourceHandleEnum.SOURCE_LOGS,
    placeholder: true,
  })

  const spanEdge = createEdge({
    source: isLeft ? parentNodeId : currentNodeId,
    target: newNodeId,
    targetHandle: TargetHandleEnum.TARGET_SPANS,
    sourceHandle: SourceHandleEnum.SOURCE_SPANS,
    placeholder: true,
  })

  const metricsEdge = createEdge({
    source: isLeft ? parentNodeId : currentNodeId,
    target: newNodeId,
    targetHandle: TargetHandleEnum.TARGET_METRICS,
    sourceHandle: SourceHandleEnum.SOURCE_METRICS,
    placeholder: true,
  })

  const arrayToUpdate = isLeft ? [{ id: currentNodeId }] : childNodeIds

  const updatedEdges = arrayToUpdate.map(({ id: nodeId }) => {
    const updatedLogEdge = createEdge({
      source: newNodeId,
      target: nodeId,
      targetHandle: TargetHandleEnum.TARGET_LOGS,
      sourceHandle: SourceHandleEnum.SOURCE_LOGS,
      placeholder: true,
    })

    const updatedSpanEdge = createEdge({
      source: newNodeId,
      target: nodeId,
      targetHandle: TargetHandleEnum.TARGET_SPANS,
      sourceHandle: SourceHandleEnum.SOURCE_SPANS,
      placeholder: true,
    })

    const updatedMetricsEdge = createEdge({
      source: newNodeId,
      target: nodeId,
      targetHandle: TargetHandleEnum.TARGET_METRICS,
      sourceHandle: SourceHandleEnum.SOURCE_METRICS,
      placeholder: true,
    })

    return [updatedLogEdge, updatedSpanEdge, updatedMetricsEdge]
  })

  // Remove old parent edge attached to current node
  setEdges((edges) => {
    return edges
      .filter((edge) => (isLeft ? !edge.target.includes(currentNodeId) : !edge.source.includes(currentNodeId)))
      .concat([logEdge, spanEdge, metricsEdge])
      .concat(updatedEdges.flat())
  })
  setNodes((nodes) => {
    const updatedNodes = nodes.map((node) => {
      const updatedNode = arrayToUpdate.find(({ id }) => id === node.id)
      if (updatedNode) {
        return {
          ...node,
          data: {
            ...node.data,
            modified: true,
            parentSliceId: newNodeId,
          },
        }
      }
      return node
    })

    return updatedNodes.concat([newNode])
  })

  setRefreshLayout(true)

  return newNodeId
}
